/**
 * A KPI as a dashboard widget: a Goal (progress bar) when it has a target,
 * otherwise the chart it was saved as, over the KPI's target period, with the
 * KPI's metric, filters and title. "Add To Dashboard" appends it to the end
 * of the dashboard's first page and saves.
 */

import { placeWidget, type WidgetSeed } from '../reports/new-widget.ts';
import { reportsApi, type Catalogue, type RangeSetting } from '../reports/reports-api.ts';
import type { Kpi } from './kpis-api.ts';

const PERIOD_RANGE: Record<string, RangeSetting> = {
  DAILY: { preset: 'TODAY' },
  WEEKLY: { preset: 'LAST_7_DAYS', includeToday: true },
  MONTHLY: { preset: 'THIS_MONTH' },
  QUARTERLY: { preset: 'THIS_QUARTER' },
  ANNUALLY: { preset: 'THIS_YEAR' },
};

function rangeOf(kpi: Kpi): RangeSetting {
  if (kpi.target_type === 'ONE_TIME' && kpi.target_start)
    return { preset: 'CUSTOM', from: kpi.target_start, to: kpi.target_due ?? kpi.target_start };
  return PERIOD_RANGE[kpi.target_period ?? 'MONTHLY'] ?? { preset: 'THIS_MONTH' };
}

export function seedForKpi(kpi: Kpi): WidgetSeed {
  const target =
    kpi.last_target ?? (kpi.target_condition?.endsWith('_BY') ? null : kpi.target_value);
  const hasTarget = kpi.target_type !== null && target !== null;
  return {
    type: hasTarget ? 'GOAL' : kpi.visual,
    data: {
      source: kpi.source,
      metrics: [kpi.metric],
      filters: kpi.filters,
      dimension: hasTarget ? null : kpi.secondary_dimension,
      range: rangeOf(kpi),
      ...(hasTarget ? { target: target ?? undefined } : {}),
    },
    display: { title: kpi.title },
  };
}

/** Appends the KPI to a dashboard and saves it; returns false when the page has no room. */
export async function addKpiToDashboard(
  organisationId: string,
  documentId: string,
  kpi: Kpi,
  catalogue: Catalogue,
): Promise<boolean> {
  const { document, pages } = await reportsApi.get(organisationId, documentId);
  const [first, ...rest] = pages;
  if (!first) return false;
  const placed = placeWidget(seedForKpi(kpi), catalogue, first.widgets, {
    maxRows: null,
    autoAlign: true,
  });
  if (!placed) return false;
  await reportsApi.save(organisationId, document.id, {
    revision: document.revision,
    name: document.name,
    settings: document.settings,
    theme: document.theme,
    pages: [{ ...first, widgets: placed.widgets }, ...rest],
    note: `Added KPI ${kpi.title}`,
  });
  return true;
}
